import { create } from "zustand";
import {
  buildSystemAmendmentReason,
  type AmendmentResponsibleSnapshot,
  type OrganizationRequisitesChanges,
} from "./amendment-requisites";
import {
  validateAmendmentRequest,
  type AmendmentValidationError,
} from "./amendment-validation";
import {
  getNextAmendmentStatus,
  isActiveAmendmentStatus,
  type AmendmentAction,
  type JournalsAmendmentsStatus,
} from "./amendments-status";

export type AmendmentDocumentType = "PERMIT" | "EXECUTIVE" | "SUPPORTING";
export type AmendmentRequestSource = "SYSTEM" | "MANUAL";
export type AmendmentViewerRole = "FILLER" | "SIGNER";

export interface CreateSystemAmendmentInput {
  titleId: string;
  changes: OrganizationRequisitesChanges;
  versionNumber?: number;
  legalChangeDate?: string | null;
}

export interface AmendmentRequest {
  key: string;
  titleId: string;
  versionNumber: number;
  requestNumber: number;
  changeType: string;
  requestSource: AmendmentRequestSource;
  reason: string;
  basis: string;
  changedBy: string;
  checkedBy: string;
  checkedVerified: boolean;
  changesList: OrganizationRequisitesChanges | null;
  dateIso: string;
  updatedAt: string;
  legalChangeDate: string | null;
  status: JournalsAmendmentsStatus;
  documentType: AmendmentDocumentType;
  document: string | null;
  additionalFile: string | null;
  additionalFileSize: number | null;
  responsible: string;
  responsiblePosition: string;
  responsibleSnapshot: AmendmentResponsibleSnapshot | null;
  rejectionComment: string | null;
  rejectionAuthor: string | null;
  signedAt: string | null;
  authorizedSignaturesRequired: number;
  authorizedSignaturesCompleted: number;
  isSuperseded: boolean;
  supersededAt: string | null;
  viewerRole: AmendmentViewerRole;
}

export type AmendmentRequestPatch = Partial<
  Pick<
    AmendmentRequest,
    | "reason"
    | "basis"
    | "documentType"
    | "document"
    | "additionalFile"
    | "additionalFileSize"
    | "legalChangeDate"
    | "responsible"
    | "responsiblePosition"
    | "responsibleSnapshot"
    | "authorizedSignaturesRequired"
    | "viewerRole"
  >
>;

export type AmendmentOperationResult =
  | { ok: true; request: AmendmentRequest }
  | {
      ok: false;
      error:
        | "NOT_FOUND"
        | "INVALID_TRANSITION"
        | "VALIDATION_ERROR"
        | "SUPERSEDED";
      validationErrors?: AmendmentValidationError[];
    };

interface AmendmentsStore {
  requests: AmendmentRequest[];
  createDraft: (
    titleId: string,
    changedBy?: string,
    versionNumber?: number,
  ) => AmendmentRequest;
  createSystemRequest: (input: CreateSystemAmendmentInput) => AmendmentRequest;
  updateRequest: (
    key: string,
    patch: AmendmentRequestPatch,
  ) => AmendmentOperationResult;
  applyAction: (
    key: string,
    action: AmendmentAction,
    options?: { comment?: string; signerName?: string },
  ) => AmendmentOperationResult;
  findActiveRequest: (titleId: string) => AmendmentRequest | null;
}

const PROJECT_BUREAU_CHANGES: OrganizationRequisitesChanges = {
  organizationId: "project-bureau",
  organizationName: "ООО “Проектное бюро”",
  organizationRole: "Лицо, осуществляющее подготовку проектной документации",
  titleSectionId: "documentation-contractor-company",
  requisites: [
    {
      field: "NAME",
      label: "Наименование организации",
      value: "ООО “Проектное бюро”",
    },
    { field: "INN", label: "ИНН", value: "7801234567" },
    { field: "KPP", label: "КПП", value: "783901001" },
    { field: "OGRN", label: "ОГРН", value: "1027800000000" },
    { field: "CONTACTS", label: "Контакты", value: "8 (812) 123-45-67" },
    {
      field: "LEGAL_ADDRESS",
      label: "Юридический адрес",
      value: "г. Санкт-Петербург, Невский пр., д. 10",
    },
  ],
  fields: [
    {
      field: "NAME",
      label: "Наименование организации",
      oldValue: "ООО “Проектное бюро”",
      newValue: "ООО “Строительный мир”",
    },
    {
      field: "KPP",
      label: "КПП",
      oldValue: "783901001",
      newValue: "738901002",
    },
    {
      field: "LEGAL_ADDRESS",
      label: "Юридический адрес",
      oldValue: "г. Санкт-Петербург, Невский пр., д. 10",
      newValue: "г. Санкт-Петербург, Лиговский пр., д. 24",
    },
  ],
};

const DEVELOPER_CHANGES: OrganizationRequisitesChanges = {
  organizationId: "developer-company",
  organizationName: "ООО “Застройщик”",
  organizationRole: "Застройщик",
  titleSectionId: "developer-company",
  requisites: [
    {
      field: "NAME",
      label: "Наименование организации",
      value: "ООО “Застройщик”",
    },
    { field: "INN", label: "ИНН", value: "7812345678" },
    { field: "KPP", label: "КПП", value: "781201001" },
    { field: "OGRN", label: "ОГРН", value: "1027800000001" },
    { field: "CONTACTS", label: "Контакты", value: "8 (921) 123-32-10" },
    {
      field: "LEGAL_ADDRESS",
      label: "Юридический адрес",
      value: "г. Санкт-Петербург, Московский пр., д. 15",
    },
  ],
  fields: [
    {
      field: "CONTACTS",
      label: "Контакты",
      oldValue: "8 (921) 123-32-10",
      newValue: "8 (921) 555-10-20",
    },
  ],
};

const MOCK_REQUESTS: AmendmentRequest[] = [
  {
    key: "system-new",
    titleId: "demo-title",
    versionNumber: 5,
    requestNumber: 4,
    changeType: "Реквизиты",
    requestSource: "SYSTEM",
    reason: buildSystemAmendmentReason(PROJECT_BUREAU_CHANGES),
    basis: "",
    changedBy: "Система",
    checkedBy: "—",
    checkedVerified: false,
    changesList: PROJECT_BUREAU_CHANGES,
    dateIso: "2026-09-18T07:30:00.000Z",
    updatedAt: "2026-09-18T07:30:00.000Z",
    legalChangeDate: null,
    status: "NEW",
    documentType: "PERMIT",
    document: null,
    additionalFile: null,
    additionalFileSize: null,
    responsible: "",
    responsiblePosition: "",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: null,
    authorizedSignaturesRequired: 1,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "FILLER",
  },
  {
    key: "system-signing",
    titleId: "demo-title",
    versionNumber: 5,
    requestNumber: 3,
    changeType: "Реквизиты",
    requestSource: "SYSTEM",
    reason: buildSystemAmendmentReason(DEVELOPER_CHANGES),
    basis: "Выписка из ЕГРЮЛ",
    changedBy: "Система",
    checkedBy: "—",
    checkedVerified: false,
    changesList: DEVELOPER_CHANGES,
    dateIso: "2026-09-17T08:00:00.000Z",
    updatedAt: "2026-09-17T09:00:00.000Z",
    legalChangeDate: "2026-09-16T00:00:00.000Z",
    status: "SIGNING_CHANGES",
    documentType: "PERMIT",
    document: "Выписка из ЕГРЮЛ.pdf",
    additionalFile: null,
    additionalFileSize: null,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: null,
    authorizedSignaturesRequired: 1,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "SIGNER",
  },
  {
    key: "1",
    titleId: "demo-title",
    versionNumber: 4,
    requestNumber: 2,
    changeType: "Реквизиты",
    requestSource: "SYSTEM",
    reason: "Неправильный ИНН",
    basis: "Приказ №145 от 01.04.2025",
    changedBy: "Гусейнов М.О.",
    checkedBy: "Гусейнов М.О.",
    checkedVerified: true,
    changesList: PROJECT_BUREAU_CHANGES,
    dateIso: "2025-06-16T10:12:00.000Z",
    updatedAt: "2025-06-16T10:15:00.000Z",
    legalChangeDate: "2025-06-07T00:00:00.000Z",
    status: "PUBLISHED",
    documentType: "PERMIT",
    document: "ДокументИзДокументария_РазделНормативка",
    additionalFile: "Документ_из_компьютера.pdf",
    additionalFileSize: 524_288,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: "2025-06-16T10:15:00.000Z",
    authorizedSignaturesRequired: 0,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "FILLER",
  },
  {
    key: "2",
    titleId: "status-demo-title",
    versionNumber: 5,
    requestNumber: 5,
    changeType: "Иные изменения",
    requestSource: "MANUAL",
    reason: "Корректировка сведений о застройщике",
    basis: "Перемен требуют наши сердца, перемен требуют наши глаза",
    changedBy: "Иванов П.С.",
    checkedBy: "—",
    checkedVerified: false,
    changesList: null,
    dateIso: "2025-06-15T08:00:00.000Z",
    updatedAt: "2025-06-15T08:00:00.000Z",
    legalChangeDate: null,
    status: "NEW",
    documentType: "EXECUTIVE",
    document: null,
    additionalFile: null,
    additionalFileSize: null,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: null,
    authorizedSignaturesRequired: 2,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "FILLER",
  },
  {
    key: "3",
    titleId: "status-demo-title",
    versionNumber: 4,
    requestNumber: 4,
    changeType: "Иные изменения",
    requestSource: "MANUAL",
    reason: "Изменение паспортных данных представителя",
    basis: "Смена руководителя, протокол собрания",
    changedBy: "Гусейнов М.О.",
    checkedBy: "—",
    checkedVerified: false,
    changesList: null,
    dateIso: "2025-06-14T14:30:00.000Z",
    updatedAt: "2025-06-14T14:35:00.000Z",
    legalChangeDate: null,
    status: "SIGNING_CHANGES",
    documentType: "PERMIT",
    document: "ДокументНаСогласование",
    additionalFile: null,
    additionalFileSize: null,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: null,
    authorizedSignaturesRequired: 1,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "SIGNER",
  },
  {
    key: "4",
    titleId: "demo-title",
    versionNumber: 3,
    requestNumber: 1,
    changeType: "Реквизиты",
    requestSource: "SYSTEM",
    reason: "Всё с маленькой буквы",
    basis: "Решение совета директоров от 10.05.2025",
    changedBy: "Петрова А.В.",
    checkedBy: "Гусейнов М.О.",
    checkedVerified: true,
    changesList: DEVELOPER_CHANGES,
    dateIso: "2025-06-16T10:12:00.000Z",
    updatedAt: "2025-06-16T10:10:00.000Z",
    legalChangeDate: "2025-06-06T00:00:00.000Z",
    status: "REJECTED",
    documentType: "SUPPORTING",
    document: "ДокументОтклоненный",
    additionalFile: null,
    additionalFileSize: null,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: "Надо поменять уполномоченного",
    rejectionAuthor: "Казикова Е.В.",
    signedAt: "2025-06-13T11:08:00.000Z",
    authorizedSignaturesRequired: 1,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "FILLER",
  },
  {
    key: "5",
    titleId: "status-demo-title",
    versionNumber: 2,
    requestNumber: 2,
    changeType: "Иные изменения",
    requestSource: "MANUAL",
    reason: "Уточнение адреса объекта",
    basis: "Выписка из ЕГРН",
    changedBy: "Иванов П.С.",
    checkedBy: "Гусейнов М.О.",
    checkedVerified: true,
    changesList: null,
    dateIso: "2025-06-12T09:40:00.000Z",
    updatedAt: "2025-06-12T09:45:00.000Z",
    legalChangeDate: null,
    status: "PREPARING_XML",
    documentType: "PERMIT",
    document: null,
    additionalFile: "Скан_документа.pdf",
    additionalFileSize: 1_048_576,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: "2025-06-12T09:45:00.000Z",
    authorizedSignaturesRequired: 2,
    authorizedSignaturesCompleted: 0,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "FILLER",
  },
  {
    key: "6",
    titleId: "status-demo-title",
    versionNumber: 1,
    requestNumber: 1,
    changeType: "Иные изменения",
    requestSource: "MANUAL",
    reason: "Изменение контактных данных",
    basis: "Приказ о смене ответственного",
    changedBy: "Сидоров К.Н.",
    checkedBy: "Застроев Г. А.",
    checkedVerified: true,
    changesList: null,
    dateIso: "2025-06-11T16:22:00.000Z",
    updatedAt: "2025-06-11T16:30:00.000Z",
    legalChangeDate: null,
    status: "ON_SIGNING",
    documentType: "EXECUTIVE",
    document: "Подписанный_документ",
    additionalFile: null,
    additionalFileSize: null,
    responsible: "Застроев Г. А.",
    responsiblePosition: "Начальник участка",
    responsibleSnapshot: null,
    rejectionComment: null,
    rejectionAuthor: null,
    signedAt: "2025-06-11T16:28:00.000Z",
    authorizedSignaturesRequired: 2,
    authorizedSignaturesCompleted: 1,
    isSuperseded: false,
    supersededAt: null,
    viewerRole: "SIGNER",
  },
];

const getNowIso = () => new Date().toISOString();

export const useAmendmentsStore = create<AmendmentsStore>((set, get) => ({
  requests: MOCK_REQUESTS,
  createDraft: (
    titleId,
    changedBy = "Текущий пользователь",
    versionNumber = 1,
  ) => {
    const activeRequest = get().findActiveRequest(titleId);
    if (activeRequest) {
      return activeRequest;
    }

    const requestNumber =
      Math.max(
        0,
        ...get()
          .requests.filter((request) => request.titleId === titleId)
          .map((request) => request.requestNumber),
      ) + 1;
    const timestamp = getNowIso();
    const request: AmendmentRequest = {
      key: `amendment-${titleId}-${requestNumber}`,
      titleId,
      versionNumber,
      requestNumber,
      changeType: "Иные изменения",
      requestSource: "MANUAL",
      reason: "",
      basis: "",
      changedBy,
      checkedBy: "—",
      checkedVerified: false,
      changesList: null,
      dateIso: timestamp,
      updatedAt: timestamp,
      legalChangeDate: null,
      status: "NEW",
      documentType: "PERMIT",
      document: null,
      additionalFile: null,
      additionalFileSize: null,
      responsible: "",
      responsiblePosition: "",
      responsibleSnapshot: null,
      rejectionComment: null,
      rejectionAuthor: null,
      signedAt: null,
      authorizedSignaturesRequired: 0,
      authorizedSignaturesCompleted: 0,
      isSuperseded: false,
      supersededAt: null,
      viewerRole: "FILLER",
    };

    set((state) => ({ requests: [request, ...state.requests] }));
    return request;
  },
  createSystemRequest: ({
    titleId,
    changes,
    versionNumber = 1,
    legalChangeDate = null,
  }) => {
    const timestamp = getNowIso();
    const requestNumber =
      Math.max(
        0,
        ...get()
          .requests.filter((request) => request.titleId === titleId)
          .map((request) => request.requestNumber),
      ) + 1;

    const request: AmendmentRequest = {
      key: `system-amendment-${titleId}-${changes.organizationId}-${requestNumber}`,
      titleId,
      versionNumber,
      requestNumber,
      changeType: "Реквизиты",
      requestSource: "SYSTEM",
      reason: buildSystemAmendmentReason(changes),
      basis: "",
      changedBy: "Система",
      checkedBy: "—",
      checkedVerified: false,
      changesList: changes,
      dateIso: timestamp,
      updatedAt: timestamp,
      legalChangeDate,
      status: "NEW",
      documentType: "PERMIT",
      document: null,
      additionalFile: null,
      additionalFileSize: null,
      responsible: "",
      responsiblePosition: "",
      responsibleSnapshot: null,
      rejectionComment: null,
      rejectionAuthor: null,
      signedAt: null,
      authorizedSignaturesRequired: 1,
      authorizedSignaturesCompleted: 0,
      isSuperseded: false,
      supersededAt: null,
      viewerRole: "FILLER",
    };

    set((state) => ({
      requests: [
        request,
        ...state.requests.map((item) =>
          item.titleId === titleId &&
          item.requestSource === "SYSTEM" &&
          item.changesList?.organizationId === changes.organizationId &&
          item.status !== "PUBLISHED" &&
          !item.isSuperseded
            ? {
                ...item,
                isSuperseded: true,
                supersededAt: timestamp,
                updatedAt: timestamp,
              }
            : item,
        ),
      ],
    }));
    return request;
  },
  updateRequest: (key, patch) => {
    const request = get().requests.find((item) => item.key === key);
    if (!request) {
      return { ok: false, error: "NOT_FOUND" };
    }
    if (request.isSuperseded) {
      return { ok: false, error: "SUPERSEDED" };
    }
    if (request.status !== "NEW") {
      return { ok: false, error: "INVALID_TRANSITION" };
    }

    const updatedRequest = {
      ...request,
      ...patch,
      updatedAt: getNowIso(),
    };
    set((state) => ({
      requests: state.requests.map((item) =>
        item.key === key ? updatedRequest : item,
      ),
    }));
    return { ok: true, request: updatedRequest };
  },
  applyAction: (key, action, options) => {
    const request = get().requests.find((item) => item.key === key);
    if (!request) {
      return { ok: false, error: "NOT_FOUND" };
    }
    if (request.isSuperseded) {
      return { ok: false, error: "SUPERSEDED" };
    }

    if (action === "SUBMIT") {
      const validationErrors = validateAmendmentRequest(request);
      if (validationErrors.length > 0) {
        return {
          ok: false,
          error: "VALIDATION_ERROR",
          validationErrors,
        };
      }
    }

    if (action === "REJECT" && !options?.comment?.trim()) {
      return {
        ok: false,
        error: "VALIDATION_ERROR",
        validationErrors: [],
      };
    }

    const systemNextStatus = (() => {
      if (request.requestSource !== "SYSTEM") return null;
      if (request.status === "NEW" && action === "SAVE") return "NEW";
      if (request.status === "NEW" && action === "SUBMIT") {
        return "SIGNING_CHANGES";
      }
      if (
        (request.status === "NEW" || request.status === "SIGNING_CHANGES") &&
        action === "REJECT"
      ) {
        return "REJECTED";
      }
      if (request.status === "SIGNING_CHANGES" && action === "WITHDRAW") {
        return "NEW";
      }
      if (request.status === "SIGNING_CHANGES" && action === "SIGN_CHANGES") {
        return "PUBLISHED";
      }
      if (request.status === "REJECTED" && action === "RETURN_TO_WORK") {
        return "NEW";
      }
      return null;
    })();
    const defaultNextStatus =
      request.requestSource === "SYSTEM"
        ? systemNextStatus
        : getNextAmendmentStatus(request.status, action);
    const isFinalAuthorizedSignature =
      action !== "SIGN_AUTHORIZED" ||
      request.authorizedSignaturesCompleted + 1 >=
        request.authorizedSignaturesRequired;
    const nextStatus =
      action === "SIGN_AUTHORIZED" && !isFinalAuthorizedSignature
        ? "ON_SIGNING"
        : defaultNextStatus;
    if (!nextStatus) {
      return { ok: false, error: "INVALID_TRANSITION" };
    }

    const timestamp = getNowIso();
    const updatedRequest: AmendmentRequest = {
      ...request,
      status: nextStatus,
      updatedAt: timestamp,
      rejectionComment:
        action === "REJECT" ? options?.comment?.trim() || null : null,
      rejectionAuthor:
        action === "REJECT"
          ? options?.signerName || request.responsible || request.changedBy
          : null,
      checkedBy:
        action === "SIGN_CHANGES"
          ? options?.signerName || request.responsible
          : request.checkedBy,
      checkedVerified:
        action === "SIGN_CHANGES" ? true : request.checkedVerified,
      signedAt: action === "SIGN_CHANGES" ? timestamp : request.signedAt,
      authorizedSignaturesCompleted:
        action === "SIGN_AUTHORIZED"
          ? request.authorizedSignaturesCompleted + 1
          : request.authorizedSignaturesCompleted,
    };

    set((state) => ({
      requests: state.requests.map((item) =>
        item.key === key ? updatedRequest : item,
      ),
    }));
    return { ok: true, request: updatedRequest };
  },
  findActiveRequest: (titleId) =>
    get().requests.find(
      (request) =>
        request.titleId === titleId &&
        !request.isSuperseded &&
        isActiveAmendmentStatus(request.status),
    ) ?? null,
}));
